import React, { useState, useEffect, useMemo } from 'react'
import { Map, useMap } from '@vis.gl/react-google-maps'

import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { DeckProps } from '@deck.gl/core'
import { EventsByLocationItem } from 'api/carrierSafetyEvents'
import { HeatmapLayer } from '@deck.gl/aggregation-layers'
import { getHeatmapColorRange, DEFAULT_MAP_CENTER, DEFAULT_MAP_ZOOM } from 'tools/mapHelper'

interface ILocationChartProps {
  safetyEventsList?: EventsByLocationItem[] | null
}

const DeckGLOverlay = (props: DeckProps) => {
  const map = useMap()
  const overlay = useMemo(() => new GoogleMapsOverlay(props), [])

  useEffect(() => {
    if (map) {
      overlay.setMap(map)
      return () => overlay.setMap(null)
    }
  }, [map, overlay])

  return null
}

const LocationChart: React.FC<ILocationChartProps> = (props) => {
  const { safetyEventsList } = props
  const map = useMap()
  const [defaultZoom] = useState(DEFAULT_MAP_ZOOM)
  const [defaultCenter] = useState(DEFAULT_MAP_CENTER)

  useEffect(() => {
    if (safetyEventsList && map) {
      map.setCenter(DEFAULT_MAP_CENTER)
      map.setZoom(DEFAULT_MAP_ZOOM)
    }
  }, [safetyEventsList, map])

  const safetyEventsData = useMemo(
    () =>
      safetyEventsList?.map((event) => ({
        position: [event.longitude, event.latitude],
        id: event.id,
        type: event.type,
        weight: 1,
      })) || [],
    [safetyEventsList]
  )

  const layers = useMemo(
    () => [
      new HeatmapLayer({
        data: safetyEventsData,
        id: 'heatmap-layer',
        pickable: false,
        getPosition: (d) => d.position,
        getWeight: (d) => d.weight,
        radiusPixels: 30,
        intensity: 4,
        threshold: 0.01,
        weightsTextureSize: 512,
        debounceTimeout: 300,
        colorRange: getHeatmapColorRange(),
      }),
    ],
    [safetyEventsData]
  )

  return (
    <Map
      defaultZoom={defaultZoom}
      defaultCenter={defaultCenter}
      gestureHandling={'greedy'}
      disableDefaultUI={true}
      clickableIcons={false}
      mapId={'SAFETY_EVENTS_MAP'}
    >
      <DeckGLOverlay layers={layers} />
    </Map>
  )
}

export default LocationChart
